import { useState } from 'react';
import {
  type GqlStripeAcceptQuoteMutation,
  useStripeCreateSubscriptionQuoteMutation,
} from '../../graphql/react-operations';
import { QuoteDialog } from './QuoteDialog';
import { isNil } from 'lodash-es';
import { PaymentDialog } from './PaymentDialog';
import { LoadingDialog } from '../simple-dialogs/LoadingDialog/LoadingDialog';
import { useOnce } from '@startuptools/common/react-hooks';

interface Props {
  companyId: string;
  meetingId?: string;
  subPriceId: string;
  subProdId: string;
  onCancel: () => void;
  onPaid: () => void;
}

export function SubscriptionQuote({ subPriceId, subProdId, meetingId, onCancel, onPaid, companyId }: Props) {
  const [invoice, setInvoice] = useState<GqlStripeAcceptQuoteMutation['stripeAcceptQuote'] | null>(null);
  const [createQuote, { loading, data }] = useStripeCreateSubscriptionQuoteMutation({
    fetchPolicy: 'no-cache',
  });
  useOnce(() => {
    void createQuote({
      variables: { companyId, input: { subPriceId, subProdId, meetingId } },
      fetchPolicy: 'no-cache',
    });
  });
  const quote = data?.stripeCreateSubscriptionQuote;
  if (loading || isNil(quote)) {
    return <LoadingDialog />;
  }

  const handleAcceptQuote = (invoice: GqlStripeAcceptQuoteMutation['stripeAcceptQuote']) => {
    if (invoice.status === 'paid') {
      onPaid();
      return;
    }
    setInvoice(invoice);
  };

  if (isNil(invoice)) {
    return (
      <QuoteDialog quoteId={quote.id} {...{ companyId, onCancel }} onAccept={invoice => handleAcceptQuote(invoice)} />
    );
  }

  const handleOnCancel = () => {
    setInvoice(null);
    onCancel();
  };

  const handleOnPaid = () => {
    setInvoice(null);
    onPaid();
  };

  if (!isNil(invoice)) {
    return (
      <PaymentDialog
        companyId={companyId}
        paymentIntentId={invoice.payment_intent}
        onCancel={() => handleOnCancel()}
        onPaid={() => handleOnPaid()}
      ></PaymentDialog>
    );
  }
}
